import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trash2, Minus, Plus, ArrowLeft, ShoppingBag, Tag, X, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { useState } from 'react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartPage = () => {
  const { items, removeItem, updateQuantity, totalPrice, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    
    setPromoLoading(true);
    try {
      const res = await axios.post(`${API}/promo/validate`, {
        code: promoCode.trim(),
        subtotal: totalPrice
      });
      
      setAppliedPromo(res.data);
      toast.success(`Promo code applied! You save $${res.data.discount_amount.toFixed(2)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid promo code');
      setAppliedPromo(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCode('');
    toast.success('Promo code removed');
  };

  const handleCheckout = async () => {
    if (items.length === 0) return;
    
    setLoading(true);
    try {
      const cartItems = items.map(item => ({
        product_id: item.product.id,
        quantity: item.quantity
      }));
      
      const res = await axios.post(`${API}/checkout/create-session`, {
        cart_items: cartItems,
        origin_url: window.location.origin,
        promo_code: appliedPromo?.code || null
      });
      
      // Redirect to Stripe
      window.location.href = res.data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error(err.response?.data?.detail || 'Failed to create checkout session');
    } finally {
      setLoading(false);
    }
  };

  const finalTotal = appliedPromo ? appliedPromo.final_total : totalPrice;
  const discountAmount = appliedPromo ? appliedPromo.discount_amount : 0;

  if (items.length === 0) {
    return (
      <div className="min-h-screen pt-28 pb-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center py-20">
          <ShoppingBag className="h-16 w-16 text-muted-foreground mx-auto mb-6" />
          <h1 className="font-heading text-3xl mb-4">Your bag is empty</h1>
          <p className="text-muted-foreground mb-8">
            Looks like you haven't added anything to your bag yet.
          </p>
          <Button
            asChild
            className="bg-[#D4AF37] text-black hover:bg-[#B5952F] px-10 py-6 text-xs tracking-widest uppercase font-medium"
          >
            <Link to="/shop">Start Shopping</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-24 px-6 md:px-12">
      <div className="max-w-6xl mx-auto">
        {/* Back Link */}
        <Link
          to="/shop"
          className="inline-flex items-center text-muted-foreground hover:text-[#D4AF37] transition-colors mb-8 text-sm"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Continue Shopping
        </Link>

        <h1 className="font-heading text-3xl md:text-4xl mb-12" data-testid="cart-title">
          Shopping Bag
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6" data-testid="cart-items">
            {items.map((item, i) => (
              <motion.div
                key={item.product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="flex gap-6 pb-6 border-b border-white/10"
              >
                {/* Image */}
                <Link
                  to={`/product/${item.product.id}`}
                  className="w-24 h-32 flex-shrink-0 bg-secondary/30 overflow-hidden"
                >
                  <img
                    src={item.product.image_url}
                    alt={item.product.name}
                    className="w-full h-full object-cover"
                  />
                </Link>

                {/* Details */}
                <div className="flex-1 flex flex-col">
                  <div className="flex justify-between">
                    <div>
                      <Link
                        to={`/product/${item.product.id}`}
                        className="font-body text-sm hover:text-[#D4AF37] transition-colors"
                      >
                        {item.product.name}
                      </Link>
                      <p className="text-xs text-muted-foreground mt-1 capitalize">
                        {item.product.category}
                      </p>
                    </div>
                    <button
                      onClick={() => removeItem(item.product.id)}
                      className="text-muted-foreground hover:text-red-500 transition-colors"
                      data-testid={`remove-${item.product.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="mt-auto flex items-center justify-between">
                    {/* Quantity */}
                    <div className="flex items-center border border-white/20">
                      <button
                        onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                        className="p-2 hover:bg-white/5 transition-colors"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="w-8 text-center text-sm">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                        className="p-2 hover:bg-white/5 transition-colors"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>

                    {/* Price */}
                    <p className="font-accent text-[#D4AF37]">
                      ${(item.product.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-white/10 p-6 sticky top-28">
              <h2 className="font-heading text-xl mb-6">Order Summary</h2>

              {/* Promo Code Input */}
              <div className="mb-6">
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Promo Code
                </label>
                {appliedPromo ? (
                  <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 p-3">
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      <span className="text-sm font-medium text-green-500">{appliedPromo.code}</span>
                      <span className="text-xs text-muted-foreground">
                        ({appliedPromo.discount_type === 'percentage' 
                          ? `${appliedPromo.discount_value}% off` 
                          : `$${appliedPromo.discount_value} off`})
                      </span>
                    </div>
                    <button
                      onClick={handleRemovePromo}
                      className="text-muted-foreground hover:text-red-500 transition-colors"
                      data-testid="remove-promo-btn"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="bg-transparent border-white/20 focus:border-[#D4AF37] uppercase"
                      data-testid="promo-code-input"
                    />
                    <Button
                      onClick={handleApplyPromo}
                      disabled={promoLoading || !promoCode.trim()}
                      variant="outline"
                      className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-4"
                      data-testid="apply-promo-btn"
                    >
                      {promoLoading ? '...' : <Tag className="h-4 w-4" />}
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>${totalPrice.toFixed(2)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-500">Discount</span>
                    <span className="text-green-500">-${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="text-[#D4AF37]">Free</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tax</span>
                  <span>Calculated at checkout</span>
                </div>
              </div>

              <div className="border-t border-white/10 pt-4 mb-6">
                <div className="flex justify-between">
                  <span className="font-body">Total</span>
                  <div className="text-right">
                    {discountAmount > 0 && (
                      <span className="text-sm text-muted-foreground line-through mr-2">
                        ${totalPrice.toFixed(2)}
                      </span>
                    )}
                    <span className="font-accent text-xl text-[#D4AF37]" data-testid="cart-total">
                      ${finalTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleCheckout}
                disabled={loading}
                className="w-full bg-[#D4AF37] text-black hover:bg-[#B5952F] py-6 text-xs tracking-widest uppercase btn-shine font-medium"
                data-testid="checkout-btn"
              >
                {loading ? 'Processing...' : 'Proceed to Checkout'}
              </Button>

              <p className="text-xs text-muted-foreground text-center mt-4">
                Secure checkout powered by Stripe
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
